import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  EVENT_CATEGORIES,
  ManagedEvent,
  ManagedEventInput,
  ParticipantScope,
  createManagedEvent,
  getManagedEvent,
  updateManagedEvent,
} from "@/features/supply-config/managed-events-api";
import { getTeams } from "@/features/supply-config/teams-api";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import {
  CopyContext,
  copyIssuesMessage,
} from "@/features/copy-tags/services/copy-tags-api";
import SchedulingDescriptionField, {
  DescriptionSection,
} from "@/features/supply-config/ui/scheduling-description-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

const EMPTY: ManagedEventInput = {
  team_ids: [],
  name: "",
  category: "SESSION",
  participant_scope: "INDIVIDUAL",
  duration_minutes: null,
  round_robin_pool_active: false,
  status: "ACTIVE",
};

export default function ManagedEventForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const prefill = (location.state as any)?.prefill as
    | { name: string; duration_minutes: number | null }
    | undefined;

  const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams });
  const { data: existing } = useQuery({
    queryKey: ["managed-event", id],
    queryFn: () => getManagedEvent(id!),
    enabled: isEdit,
  });

  const [form, setForm] = useState<ManagedEventInput>({
    ...EMPTY,
    name: prefill?.name ?? "",
    duration_minutes: prefill?.duration_minutes ?? null,
  });
  const [descSections, setDescSections] = useState<DescriptionSection[]>([]);
  const [descTitle, setDescTitle] = useState("");
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (isEdit && existing && !hydrated) {
      setForm({
        team_ids: existing.teams?.map((t) => t.id) ?? [],
        name: existing.name,
        category: existing.category,
        participant_scope: existing.participant_scope,
        duration_minutes: existing.duration_minutes,
        round_robin_pool_active: existing.round_robin_pool_active,
        status: existing.status,
        metadata: existing.metadata ?? undefined,
      });
      const sections = (existing.metadata as any)?.schedulingDescription;
      setDescSections(Array.isArray(sections) ? sections : []);
      const title = (existing.metadata as any)?.schedulingTitle;
      setDescTitle(typeof title === "string" ? title : "");
      setHydrated(true);
    }
  }, [isEdit, existing, hydrated]);

  // Booking-page copy: a bare {{RESCHEDULE_PRICE}} is this session's fee
  // (RH-1007); the customer's pack is only known when they book.
  const schedulingCopyContext: CopyContext = {
    surface: "scheduling",
    managedEventId: id || null,
  };

  const save = useMutation({
    mutationFn: async (): Promise<ManagedEvent> => {
      // Fold the description sections into metadata (preserving other keys).
      const payload: ManagedEventInput = {
        ...form,
        metadata: {
          ...(form.metadata ?? {}),
          schedulingTitle: descTitle.trim() || null,
          schedulingDescription: descSections
            .map((s) => ({
              subTitle: s.subTitle.trim(),
              content: (s.content ?? [])
                .map((c) => c.trim())
                .filter(Boolean),
              ...(s.additionalConclusion?.trim()
                ? { additionalConclusion: s.additionalConclusion.trim() }
                : {}),
            }))
            .filter((s) => s.subTitle || s.content.length),
        },
      };
      // A broken price tag must never be saved — it would hold the booking
      // page on a spinner.
      const issues = await copyIssuesMessage(schedulingCopyContext, {
        schedulingTitle: payload.metadata?.schedulingTitle,
        schedulingDescription: payload.metadata?.schedulingDescription,
      });
      if (issues) throw new Error(`Fix the price tags before saving:\n${issues}`);
      return isEdit
        ? updateManagedEvent(id!, payload)
        : createManagedEvent(payload);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["managed-events"] });
      if (isEdit) qc.invalidateQueries({ queryKey: ["managed-event", id] });
      toast({ title: isEdit ? "Managed event updated" : "Managed event created" });
      const targetId = isEdit ? id : res?.id;
      navigate(targetId ? `../${targetId}` : "..");
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || e.message || "Failed",
        variant: "destructive",
      }),
  });

  return (
    <HasAccess permission="write">
      <div className="max-w-2xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">
            {isEdit ? "Edit Managed Event" : "New Managed Event"}
          </h1>
        </div>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div>
              <p className="text-xs text-gray-500 mb-1">Name</p>
              <Input
                placeholder="Name (e.g. 1:1 Therapy 45 min)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Teams</p>
              <MultiSelect
                options={teams.map((t) => ({ label: t.name, value: t.id }))}
                value={form.team_ids}
                onChange={(v) => setForm({ ...form, team_ids: v })}
                placeholder="Select teams"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Category</p>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Attendees</p>
                <Select
                  value={form.participant_scope}
                  onValueChange={(v) =>
                    setForm({ ...form, participant_scope: v as ParticipantScope })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Attendees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                    <SelectItem value="JOINT">Joint</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Duration (minutes)</p>
              <Input
                type="number"
                placeholder="Duration (minutes)"
                value={form.duration_minutes ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    duration_minutes: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
              />
            </div>

            <div className="flex items-center justify-between border rounded-md p-3">
              <span className="text-sm">Round-robin pool active</span>
              <Switch
                checked={form.round_robin_pool_active}
                onCheckedChange={(v) =>
                  setForm({ ...form, round_robin_pool_active: v })
                }
              />
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <Select
                value={form.status}
                onValueChange={(v) =>
                  setForm({ ...form, status: v as "ACTIVE" | "INACTIVE" })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">
                Scheduling title{" "}
                <span className="text-gray-400">
                  (page heading on the booking screen; falls back to the product
                  name)
                </span>
              </p>
              <Input
                placeholder="e.g. Book your therapy session"
                value={descTitle}
                onChange={(e) => setDescTitle(e.target.value)}
              />
            </div>

            <SchedulingDescriptionField
              value={descSections}
              onChange={setDescSections}
              copyContext={schedulingCopyContext}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                onClick={() => save.mutate()}
                disabled={save.isPending || !form.name || !form.team_ids.length}
              >
                {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </HasAccess>
  );
}
